/**
 * VorgaengeUebersichtPage - Globale Vorgänge-Übersicht mit vernetztem Drill-Down
 * 
 * Navigation: Vorgangstyp → Betreute + Träger → Träger-Detail → Betreuter-Akte
 * Jeder Punkt ist klickbar und führt zum nächsten Kontext.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Landmark, ArrowLeft, Users, Euro, Search,
  Building2, FileText, ChevronRight, RefreshCw,
  Gavel, HeartPulse, FileSignature, Coins,
  ScrollText, Briefcase, FolderOpen, X, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const ICON_MAP = {
  gavel: Gavel, landmark: Landmark, 'heart-pulse': HeartPulse,
  'file-signature': FileSignature, coins: Coins, scroll: ScrollText,
  users: Briefcase, folder: FolderOpen,
};

export default function VorgaengeUebersichtPage() {
  const navigate = useNavigate();
  // Navigation stack for breadcrumb-like back navigation
  const [viewStack, setViewStack] = useState([{ type: 'katalog' }]);
  const currentView = viewStack[viewStack.length - 1];

  // Deep-Links verarbeiten (aus LebenssituationKacheln)
  useEffect(() => {
    const checkDeepLink = () => {
      const raw = localStorage.getItem('vorgaenge_deep_link');
      if (!raw) return;
      localStorage.removeItem('vorgaenge_deep_link');
      try {
        const { typ, typLabel, traeger } = JSON.parse(raw);
        if (typ) {
          setViewStack([{ type: 'katalog' }, { type: 'vorgangstyp', slug: typ, label: typLabel || typ, farbe: '#3b82f6', icon: 'landmark' }]);
        } else if (traeger) {
          setViewStack([{ type: 'katalog' }, { type: 'traeger', name: traeger }]);
        }
      } catch {}
    };
    checkDeepLink();
    window.addEventListener('vorgaenge-deep-link', checkDeepLink);
    return () => window.removeEventListener('vorgaenge-deep-link', checkDeepLink);
  }, []);

  const pushView = (view) => setViewStack(prev => [...prev, view]);
  const popView = () => setViewStack(prev => prev.length > 1 ? prev.slice(0, -1) : prev);

  const getToken = useCallback(() => {
    try {
      const stored = localStorage.getItem('olympus_auth');
      if (stored) return JSON.parse(stored).token || JSON.parse(stored).access_token || '';
    } catch {}
    return '';
  }, []);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${getToken()}`,
    'Content-Type': 'application/json',
  }), [getToken]);

  // Breadcrumb
  const breadcrumbs = viewStack.map((v, i) => {
    if (v.type === 'katalog') return { label: 'Vorgänge', index: i };
    if (v.type === 'vorgangstyp') return { label: v.label, index: i };
    if (v.type === 'traeger') return { label: v.name, index: i };
    return { label: '?', index: i };
  });

  return (
    <div className="space-y-4" data-testid="vorgaenge-uebersicht">
      {/* Breadcrumb */}
      {viewStack.length > 1 && (
        <div className="flex items-center gap-1.5 text-sm" data-testid="vorgaenge-breadcrumb">
          {breadcrumbs.map((bc, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {i > 0 && <ChevronRight className="w-3 h-3 text-slate-300" />}
              {i < breadcrumbs.length - 1 ? (
                <button
                  onClick={() => setViewStack(prev => prev.slice(0, bc.index + 1))}
                  className="text-blue-600 hover:underline"
                >
                  {bc.label}
                </button>
              ) : (
                <span className="font-medium text-slate-700">{bc.label}</span>
              )}
            </span>
          ))}
        </div>
      )}

      {currentView.type === 'katalog' && (
        <KatalogView authHeaders={authHeaders} onSelectTyp={(typ) => pushView({ type: 'vorgangstyp', slug: typ.slug, label: typ.label, farbe: typ.farbe, icon: typ.icon })} />
      )}
      {currentView.type === 'vorgangstyp' && (
        <VorgangstypView
          slug={currentView.slug} label={currentView.label}
          farbe={currentView.farbe} icon={currentView.icon}
          authHeaders={authHeaders}
          onBack={popView}
          onSelectTraeger={(name) => pushView({ type: 'traeger', name })}
          onSelectKlient={(id) => navigate(`/akten/${id}`)}
        />
      )}
      {currentView.type === 'traeger' && (
        <TraegerView
          traegerName={currentView.name}
          authHeaders={authHeaders}
          onBack={popView}
          onSelectTyp={(typ) => pushView({ type: 'vorgangstyp', slug: typ.slug, label: typ.label, farbe: typ.farbe, icon: typ.icon })}
          onSelectKlient={(id) => navigate(`/akten/${id}`)}
        />
      )}
    </div>
  );
}


// ==================== KATALOG VIEW ====================
function KatalogView({ authHeaders, onSelectTyp }) {
  const [katalog, setKatalog] = useState([]);
  const [statistik, setStatistik] = useState({});
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [katRes, statsRes] = await Promise.all([
        fetch(`${API}/api/vorgaenge/katalog`, { headers: authHeaders() }),
        fetch(`${API}/api/vorgaenge/statistik`, { headers: authHeaders() }),
      ]);
      if (katRes.ok) setKatalog(await katRes.json());
      if (statsRes.ok) setStatistik(await statsRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [authHeaders]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <div className="text-center py-16 text-slate-400">Lade Vorgänge...</div>;

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Vorgänge</h1>
          <p className="text-sm text-slate-500 mt-0.5">Alle Leistungsarten und Verfahren im Überblick</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData} data-testid="vorgaenge-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Aktualisieren
        </Button>
      </div>

      {katalog.map((kat) => {
        const KatIcon = ICON_MAP[kat.icon] || FolderOpen;
        return (
          <div key={kat.kategorie} className="space-y-3" data-testid={`vorgaenge-kat-${kat.kategorie}`}>
            <div className="flex items-center gap-2.5">
              <KatIcon className="w-5 h-5" style={{ color: kat.farbe }} />
              <h2 className="font-semibold text-slate-700">{kat.kategorie_label}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {kat.typen.map((typ) => {
                const stats = statistik[typ.slug];
                const count = stats?.anzahl_klienten || 0;
                const summe = stats?.summe_betrag || 0;
                return (
                  <button
                    key={typ.slug}
                    onClick={() => onSelectTyp({ slug: typ.slug, label: typ.label, farbe: kat.farbe, icon: kat.icon })}
                    className="group text-left p-4 rounded-xl border-2 bg-white transition-all duration-200 cursor-pointer hover:shadow-md"
                    style={{ borderColor: count > 0 ? kat.farbe + '40' : '#e2e8f0' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = kat.farbe; e.currentTarget.style.backgroundColor = kat.farbe + '06'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = count > 0 ? kat.farbe + '40' : '#e2e8f0'; e.currentTarget.style.backgroundColor = '#fff'; }}
                    data-testid={`vorgaenge-typ-${typ.slug}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-800 truncate">{typ.label}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="flex items-center gap-1 text-sm text-slate-500">
                            <Users className="w-3.5 h-3.5" /> {count}
                          </span>
                          {summe > 0 && (
                            <span className="flex items-center gap-1 text-sm text-slate-500">
                              <Euro className="w-3.5 h-3.5" /> {summe.toLocaleString('de-DE')} €
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors shrink-0 mt-1" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
}


// ==================== VORGANGSTYP VIEW (z.B. "Wohngeld") ====================
function VorgangstypView({ slug, label, farbe, icon, authHeaders, onBack, onSelectTraeger, onSelectKlient }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/vorgaenge/typ/${slug}/klienten`, { headers: authHeaders() });
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [slug, authHeaders]);

  const Icon = ICON_MAP[icon] || Landmark;
  const klienten = data?.klienten || [];
  const traeger = data?.traeger || [];
  const filtered = search
    ? klienten.filter(k =>
        k.klient_name?.toLowerCase().includes(search.toLowerCase()) ||
        k.institution?.toLowerCase().includes(search.toLowerCase()) ||
        k.aktenzeichen?.toLowerCase().includes(search.toLowerCase())
      )
    : klienten;

  return (
    <div className="space-y-6" data-testid="vorgaenge-typ-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="vorgaenge-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
        </Button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: farbe + '18' }}>
            <Icon className="w-5 h-5" style={{ color: farbe }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800" data-testid="vorgaenge-typ-title">{label}</h1>
            <p className="text-sm text-slate-500">{data?.anzahl || 0} {(data?.anzahl || 0) === 1 ? 'Betreuter' : 'Betreute'}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Lade...</div>
      ) : (
        <>
          {/* Träger-Übersicht (wenn vorhanden) */}
          {traeger.length > 0 && (
            <div data-testid="vorgaenge-traeger-section">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Träger / Institutionen</p>
              <div className="flex flex-wrap gap-2">
                {traeger.map(t => (
                  <button
                    key={t.name}
                    onClick={() => onSelectTraeger(t.name)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50 transition-all text-sm cursor-pointer"
                    data-testid={`vorgaenge-traeger-chip-${t.name}`}
                  >
                    <Building2 className="w-3.5 h-3.5 text-slate-400" />
                    <span className="font-medium">{t.name}</span>
                    <Badge variant="secondary" className="text-xs">{t.anzahl}</Badge>
                    {t.summe > 0 && <span className="text-xs text-slate-400">{t.summe.toLocaleString('de-DE')} €</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Suche */}
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input className="pl-9" placeholder="Name, Institution, Aktenzeichen..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="vorgaenge-typ-search" />
            {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-slate-400" /></button>}
          </div>

          {/* Klienten-Tabelle */}
          {filtered.length > 0 ? (
            <div className="border rounded-xl overflow-hidden bg-white">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Betreuter</TableHead>
                    <TableHead className="font-semibold">Träger / Institution</TableHead>
                    <TableHead className="font-semibold">Aktenzeichen</TableHead>
                    <TableHead className="font-semibold text-right">Betrag</TableHead>
                    <TableHead className="font-semibold">Turnus</TableHead>
                    <TableHead className="font-semibold text-center">Dok.</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((k) => (
                    <TableRow key={k.vorgang_id} className="hover:bg-slate-50 transition-colors" data-testid={`vorgaenge-klient-row-${k.klient_id}`}>
                      <TableCell>
                        <button onClick={() => onSelectKlient(k.klient_id)} className="font-medium text-blue-600 hover:underline" data-testid={`vorgaenge-goto-akte-${k.klient_id}`}>
                          {k.klient_name || '–'}
                        </button>
                      </TableCell>
                      <TableCell>
                        {k.institution ? (
                          <button onClick={() => onSelectTraeger(k.institution)} className="flex items-center gap-1.5 text-blue-600 hover:underline" data-testid={`vorgaenge-goto-traeger-${k.institution}`}>
                            <Building2 className="w-3.5 h-3.5 text-slate-400" />
                            {k.institution}
                          </button>
                        ) : <span className="text-slate-400">–</span>}
                      </TableCell>
                      <TableCell>
                        {k.aktenzeichen ? <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{k.aktenzeichen}</span> : <span className="text-slate-400">–</span>}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {k.betrag != null && k.betrag > 0 ? `${Number(k.betrag).toLocaleString('de-DE')} €` : <span className="text-slate-400">–</span>}
                      </TableCell>
                      <TableCell>{k.turnus || <span className="text-slate-400">–</span>}</TableCell>
                      <TableCell className="text-center">
                        {k.dokumente_count > 0 ? (
                          <Badge variant="secondary" className="text-xs"><FileText className="w-3 h-3 mr-0.5" />{k.dokumente_count}</Badge>
                        ) : <span className="text-slate-300">–</span>}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => onSelectKlient(k.klient_id)}>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Users className="w-10 h-10 mx-auto mb-2 text-slate-200" />
              <p>{search ? 'Keine Treffer' : 'Keine Betreuten mit diesem Vorgang'}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ==================== TRÄGER VIEW (z.B. "AOK Bayern") ====================
function TraegerView({ traegerName, authHeaders, onBack, onSelectTyp, onSelectKlient }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/vorgaenge/traeger/${encodeURIComponent(traegerName)}`, { headers: authHeaders() });
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [traegerName, authHeaders]);

  const vorgaenge = data?.vorgaenge || [];
  const filtered = search
    ? vorgaenge.filter(v =>
        v.klient_name?.toLowerCase().includes(search.toLowerCase()) ||
        v.typ_label?.toLowerCase().includes(search.toLowerCase()) ||
        v.aktenzeichen?.toLowerCase().includes(search.toLowerCase())
      )
    : vorgaenge;

  // Gruppiert nach Vorgangstyp
  const byTyp = {};
  for (const v of vorgaenge) {
    if (!byTyp[v.typ]) byTyp[v.typ] = { label: v.typ_label, farbe: v.farbe, slug: v.typ, count: 0, summe: 0 };
    byTyp[v.typ].count++;
    byTyp[v.typ].summe += v.betrag || 0;
  }

  return (
    <div className="space-y-6" data-testid="vorgaenge-traeger-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="vorgaenge-traeger-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
        </Button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800" data-testid="vorgaenge-traeger-title">{traegerName}</h1>
            <p className="text-sm text-slate-500">
              {data?.anzahl_vorgaenge || 0} Vorgänge · {(data?.summe_betrag || 0).toLocaleString('de-DE')} € gesamt
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Lade...</div>
      ) : (
        <>
          {/* Vorgangstypen dieses Trägers */}
          {Object.keys(byTyp).length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Leistungsarten</p>
              <div className="flex flex-wrap gap-2">
                {Object.values(byTyp).map(t => (
                  <button
                    key={t.slug}
                    onClick={() => onSelectTyp({ slug: t.slug, label: t.label, farbe: t.farbe, icon: 'landmark' })}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:border-blue-300 hover:bg-blue-50 transition-all text-sm cursor-pointer"
                    style={{ borderColor: t.farbe + '40' }}
                    data-testid={`traeger-typ-chip-${t.slug}`}
                  >
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: t.farbe }} />
                    <span className="font-medium">{t.label}</span>
                    <Badge variant="secondary" className="text-xs">{t.count}</Badge>
                    {t.summe > 0 && <span className="text-xs text-slate-400">{t.summe.toLocaleString('de-DE')} €</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Suche */}
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input className="pl-9" placeholder="Name, Leistungsart, Aktenzeichen..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="vorgaenge-traeger-search" />
            {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-slate-400" /></button>}
          </div>

          {/* Tabelle */}
          {filtered.length > 0 ? (
            <div className="border rounded-xl overflow-hidden bg-white">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Betreuter</TableHead>
                    <TableHead className="font-semibold">Leistungsart</TableHead>
                    <TableHead className="font-semibold">Aktenzeichen</TableHead>
                    <TableHead className="font-semibold text-right">Betrag</TableHead>
                    <TableHead className="font-semibold">Turnus</TableHead>
                    <TableHead className="font-semibold text-center">Dok.</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((v) => (
                    <TableRow key={v.vorgang_id} className="hover:bg-slate-50 transition-colors" data-testid={`traeger-vorgang-row-${v.vorgang_id}`}>
                      <TableCell>
                        <button onClick={() => onSelectKlient(v.klient_id)} className="font-medium text-blue-600 hover:underline">
                          {v.klient_name || '–'}
                        </button>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => onSelectTyp({ slug: v.typ, label: v.typ_label, farbe: v.farbe, icon: 'landmark' })}
                          className="flex items-center gap-1.5 hover:underline"
                          style={{ color: v.farbe }}
                        >
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: v.farbe }} />
                          {v.typ_label}
                        </button>
                      </TableCell>
                      <TableCell>
                        {v.aktenzeichen ? <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{v.aktenzeichen}</span> : <span className="text-slate-400">–</span>}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {v.betrag != null && v.betrag > 0 ? `${Number(v.betrag).toLocaleString('de-DE')} €` : <span className="text-slate-400">–</span>}
                      </TableCell>
                      <TableCell>{v.turnus || <span className="text-slate-400">–</span>}</TableCell>
                      <TableCell className="text-center">
                        {v.dokumente_count > 0 ? (
                          <Badge variant="secondary" className="text-xs"><FileText className="w-3 h-3 mr-0.5" />{v.dokumente_count}</Badge>
                        ) : <span className="text-slate-300">–</span>}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => onSelectKlient(v.klient_id)}>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Building2 className="w-10 h-10 mx-auto mb-2 text-slate-200" />
              <p>{search ? 'Keine Treffer' : 'Keine Vorgänge für diesen Träger'}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
